import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { JwtModule } from '@auth0/angular-jwt';
import { NgxsModule } from '@ngxs/store';
import { StorageService } from 'src/core/services/storage.service';
import { AuthState } from 'src/states/auth/state/auth.state';
import { AuthService } from './services/auth.service';
import { SharedModule } from 'src/shared/shared.module';
import { LogoutComponent } from './components/logout-button/logout.component';
import { MaintenanceMessagesApiService } from './services/maintenance-messages-api.service';
import { MaintenanceMessagesState } from './services/maintenance-messages-state';
import { MaintenanceMessageService } from './services/maintenance-messages-service';
import { HomeAccountState, HomeLocationState, HomeUserState, UserLocationState } from 'src/states';
import { AuthTokenInterceptor } from './interceptors/auth-token.interceptor';
import { LogoutSuccessComponent } from './components/logout-button/logout-success.component';
import { HomePracticeState } from 'src/states/home-practice/home-practice.state';
import { UserLocationsListState } from 'src/states/user-locations-list/user-locations-list.state';
import { AccountWelcomeState } from 'src/states/account-welcome/account-welcome.state';
import { LOCAL_STORAGE_ENGINE, NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { AppSettingsState } from 'src/states/app-settings/app-settings.state';
import { CurrentLocationSettingsState } from 'src/states/current-location-settings/current-location-settings.state';
import { ProvidersState } from 'src/app/states/providers.state';
import { PracticeLocationsState } from 'src/app/states/practice-locations-state.service';
import { LaboratoryFacilitiesState } from 'src/app/states/laboratory-facilities/laboratory-facilities.state';
import { CurrentAccountState } from 'src/states/current-account/current-account.state';
import { SplitOrdersState } from 'src/app/pages/orders/modules/order/state/split-orders-state.service';
import { AoeQuestionsState } from 'src/app/pages/orders/modules/order/state/aoe-questions.state';
import { TestGroupsState } from 'src/app/pages/orders/modules/order/state/test-groups.state';
import { OrderLastVisitedStepState } from 'src/app/states/order-last-visited-step.state';
import { ValidationSettingsState } from 'src/app/pages/orders/modules/order/state/validation-settings.state';
import { BrowserService } from 'src/core/services/browser.service';
import { FontControlService } from 'src/core/services/font-control.service';
import { MediaQueryService } from 'src/core/services/media-query.service';
import { ReportingProvidersState } from 'src/app/states/reporting-providers.state';
import { LoginClaimsState } from 'src/modules/security/states/login-claims/login-claims.state';
import { FavoriteTestsState } from 'src/app/pages/orders/modules/order/state/favorite-tests.state';
import { JumpToAccountState } from 'src/app/states/jump-to-account/jump-to-account.state';
import { PatientDemographicsState } from 'src/modules/demographics-forms/states/patients-demographics.state';
import { EmrOrderInfoState } from 'src/app/states/emr-order-info/emr-order-info.state';
import { TestCompendiumState } from 'src/app/states/test-compendium.state';
import { PscHomeLocationSettingsState } from 'src/states/psc-home-location-settings/psc-home-location-settings.state';
import { ContainersState } from 'src/app/states/containers.state';
import { SidebarPreferencesState } from 'src/app/components/side-bar/sidebar-preferences.state';
import { PatientsSearchContextState } from 'src/states/patients-search-context/patients-search-context.state';
import { VisitedLinksState } from 'src/shared/states/visited-links/visited-links.state';
import { AppInfoState } from 'src/app/states/app-info.state';

@NgModule({
  providers: [
    AuthService,
    BrowserService,
    MediaQueryService,
    StorageService,
    FontControlService,
    MaintenanceMessagesApiService,
    MaintenanceMessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true
    }
  ],
  imports: [
    LogoutComponent,
    LogoutSuccessComponent,
    CommonModule,
    HttpClientModule,
    FormsModule,
    JwtModule,
    SharedModule,
    NgxsModule.forFeature([
      AuthState,
      LoginClaimsState,
      MaintenanceMessagesState,
      HomeLocationState,
      JumpToAccountState,
      CurrentLocationSettingsState,
      PscHomeLocationSettingsState,
      HomePracticeState,
      UserLocationsListState,
      HomeUserState,
      HomeAccountState,
      AccountWelcomeState,
      CurrentAccountState,
      AppSettingsState,
      PatientDemographicsState,
      UserLocationState,
      ProvidersState,
      ReportingProvidersState,
      PracticeLocationsState,
      LaboratoryFacilitiesState,
      TestGroupsState,
      TestCompendiumState,
      FavoriteTestsState,
      OrderLastVisitedStepState,
      ValidationSettingsState,
      EmrOrderInfoState,
      ContainersState,
      PatientsSearchContextState,
      VisitedLinksState
    ]),
    NgxsStoragePluginModule.forRoot({
      keys: [
        {
          key: HomeLocationState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: CurrentAccountState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: CurrentLocationSettingsState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: HomePracticeState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: JumpToAccountState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: UserLocationsListState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: HomeUserState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: AppSettingsState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: UserLocationState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: AccountWelcomeState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: TestGroupsState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: ValidationSettingsState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: SplitOrdersState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: AoeQuestionsState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: OrderLastVisitedStepState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: LoginClaimsState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: SidebarPreferencesState,
          engine: LOCAL_STORAGE_ENGINE
        },
        {
          key: VisitedLinksState,
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: AppInfoState,
          engine: LOCAL_STORAGE_ENGINE
        }
      ]
    })
  ],
  exports: [LogoutComponent]
})
export class SecurityModule {}
